import React, { useEffect, useState } from 'react';
import appVersion from '../appVersion.json';

/**
 * Detects when a newer frontend build has been deployed and prompts the user
 * to refresh. Never force-reloads — users may have unsaved work (e.g. EditFilings).
 *
 * How it works:
 * - The bundle carries the version it was built with (src/appVersion.json).
 * - public/version.json is regenerated on every deploy (scripts/generate-version.js).
 * - We poll the served version.json every 5 minutes and on tab re-focus; when it
 *   differs from the bundled version, the prompt appears.
 */
const POLL_INTERVAL_MS = 5 * 60 * 1000;

export const NewVersionBanner: React.FC = () => {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const check = async () => {
      try {
        const res = await fetch(`/version.json?t=${Date.now()}`, { cache: 'no-store' });
        if (!res.ok) return;
        const data = await res.json();
        if (!cancelled && data?.version && data.version !== appVersion.version) {
          setUpdateAvailable(true);
        }
      } catch {
        // Offline / transient network error — ignore, next poll will retry
      }
    };

    check();
    const interval = setInterval(check, POLL_INTERVAL_MS);
    const onVisibility = () => {
      if (document.visibilityState === 'visible') check();
    };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      cancelled = true;
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, []);

  if (!updateAvailable || dismissed) return null;

  return (
    <div
      className="fixed bottom-4 right-4 z-[9998] bg-white border border-blue-200 rounded-xl shadow-lg p-4 max-w-sm flex items-start gap-3"
      role="alert"
    >
      <div className="flex-shrink-0 w-9 h-9 bg-blue-100 rounded-full flex items-center justify-center">
        <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
        </svg>
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-gray-900">Update available</p>
        <p className="text-xs text-gray-500 mt-0.5">
          A new version of the app has been released. Please save your work, then refresh to update.
        </p>
        <div className="flex gap-2 mt-3">
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="px-3 py-1.5 text-xs font-medium rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition"
          >
            Refresh now
          </button>
          <button
            type="button"
            onClick={() => setDismissed(true)}
            className="px-3 py-1.5 text-xs font-medium rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50 transition"
          >
            Later
          </button>
        </div>
      </div>
    </div>
  );
};

export default NewVersionBanner;
